import { OtpCodeInput } from '@/components/shared/SupportEmailVerification';
import { Button } from '@/components/shared/Button/Button';
import Global from '@/assets/icons/Global';
import Title from '@/components/shared/Title/Title';
import { SECURITY_CHALLENGE_OTP_LEN } from '@/hooks/useSecurityChallenge';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type LoginTwoFactorStepProps = {
  email: string;
  isLoading?: boolean;
  isResending?: boolean;
  resendCooldownSec?: number;
  error?: string | null;
  onVerify: (code: string) => void;
  onResend: () => void;
  onBack: () => void;
};

function onlyDigits(value: string) {
  return value.replace(/\D/g, '').slice(0, SECURITY_CHALLENGE_OTP_LEN);
}

export function LoginTwoFactorStep({
  email,
  isLoading = false,
  isResending = false,
  resendCooldownSec = 0,
  error,
  onVerify,
  onResend,
  onBack,
}: LoginTwoFactorStepProps) {
  const { t } = useTranslation('common');
  const [code, setCode] = useState('');
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const submittedCodeRef = useRef<string | null>(null);

  useEffect(() => {
    if (code.length !== SECURITY_CHALLENGE_OTP_LEN) return;
    if (isLoading) return;
    if (submittedCodeRef.current === code) return;

    submittedCodeRef.current = code;
    onVerify(code);
  }, [code, isLoading, onVerify]);

  useEffect(() => {
    if (!error) return;
    submittedCodeRef.current = null;
    setCode('');
    window.requestAnimationFrame(() => inputRefs.current[0]?.focus());
  }, [error]);

  const handleChange = useCallback(
    (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
      const entered = onlyDigits(e.target.value);
      setCode((prev) => {
        const current = onlyDigits(prev);
        if (entered.length > 1) {
          return onlyDigits(current.slice(0, index) + entered);
        }
        return onlyDigits(
          current.slice(0, index) + (entered || '') + current.slice(index + 1),
        );
      });
      if (entered) {
        const focusAt = Math.min(
          index + entered.length,
          SECURITY_CHALLENGE_OTP_LEN - 1,
        );
        window.requestAnimationFrame(() => inputRefs.current[focusAt]?.focus());
      }
    },
    [],
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== 'Backspace') return;
      if (e.currentTarget.value !== '' || index === 0) return;
      e.preventDefault();
      setCode((prev) => onlyDigits(prev).slice(0, index - 1));
      window.requestAnimationFrame(() => inputRefs.current[index - 1]?.focus());
    },
    [],
  );

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = onlyDigits(e.clipboardData.getData('text'));
    if (!pasted) return;
    setCode(pasted);
    const focusAt = Math.min(pasted.length, SECURITY_CHALLENGE_OTP_LEN - 1);
    window.requestAnimationFrame(() => inputRefs.current[focusAt]?.focus());
  }, []);

  const description = useMemo(
    () =>
      t('loginTwoFactorDescriptionWithEmail', {
        email,
        defaultValue: `Enviamos um código de 6 dígitos para ${email}. Digite-o para continuar.`,
      }),
    [email, t],
  );

  return (
    <div className="space-y-8 rounded-lg max-w-[416px] w-full">
      <header>
        <Global />
        <Title className="mt-3" text={t('loginTwoFactorTitle', { defaultValue: 'Verificação em duas etapas' })} />
        <p className="mt-3 text-sm text-muted leading-relaxed">{description}</p>
      </header>

      <div className="space-y-4">
        <OtpCodeInput
          length={SECURITY_CHALLENGE_OTP_LEN}
          digits={code.split('')}
          disabled={isLoading}
          inputRefs={inputRefs}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          ariaLabel={t('securityChallengeEnterCode')}
          dataCyPrefix="login-two-factor-otp"
        />

        {error && (
          <p className="text-sm text-error text-center" data-cy="login-two-factor-error">
            {error}
          </p>
        )}

        {isLoading && (
          <p className="text-sm text-muted text-center">
            {t('securityChallengeVerifying')}
          </p>
        )}

        <div className="flex flex-col gap-3">
          <Button
            type="button"
            variant="link"
            className="w-full"
            onClick={onResend}
            disabled={isLoading || isResending || resendCooldownSec > 0}
            data-cy="login-two-factor-resend"
          >
            {isResending
              ? t('securityChallengeSending')
              : resendCooldownSec > 0
                ? t('securityChallengeResendWait', { seconds: resendCooldownSec })
                : t('securityChallengeResend')}
          </Button>

          <Button
            type="button"
            variant="link"
            className="w-full"
            onClick={onBack}
            disabled={isLoading || isResending}
            data-cy="login-two-factor-back"
          >
            {t('loginTwoFactorBack', { defaultValue: 'Voltar ao login' })}
          </Button>
        </div>
      </div>
    </div>
  );
}
